import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import EntryFormHeader from '../../components/header/EntryFormHeader';
import { RootState } from '../../redux/store';
import { NEW_VERIFICATION, NEW_VERIFICATION_WITH_PARAM } from '../../graphql/queries';
import { SUBMIT_FORM_INPUT } from '../../graphql/mutations';
import { setField } from '../../redux/entryFormSlice';
import Loading from '../../components/common/Loading';

interface PersonalEntryFormGuideType {
}

declare const Trustdock: any;

const PersonalEntryFormGuide: React.FC<PersonalEntryFormGuideType> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const entryFormInfo = useSelector((state: RootState) => state.entryForm);

  useEffect(() => {
    if((entryFormInfo.entryFormStep !== 'guide') && (entryFormInfo.entryFormStep !== 'complete')) {
      dispatch(setField({field: 'entryFormStep', value: 'entry'}))
      navigate('/form/personal')
    }
  }, [entryFormInfo.entryFormStep])

  // Freeze the decision at mount: if a verification already exists in the
  // (now persisted) state — e.g. after a tab reload during the eKYC step —
  // reuse it via NEW_VERIFICATION_WITH_PARAM instead of minting a brand-new
  // verification, so the applicant's documents and form data stay on one record.
  const [existingExternalId] = useState<string>(entryFormInfo.externalId);
  const hasExistingVerification = Boolean(existingExternalId);

  const { loading, error, data } = useQuery(
    hasExistingVerification ? NEW_VERIFICATION_WITH_PARAM : NEW_VERIFICATION,
    hasExistingVerification ? { variables: { externalId: existingExternalId } } : undefined
  );

  const [submitFormInput] = useMutation(SUBMIT_FORM_INPUT);

  const [personPlanId, setPersonPlanId] = useState<string>('');
  const [submitError, setSubmitError] = useState<boolean>(false);

  const [trustdock, setTrustdock] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadedTrustdock, setIsLoadedTrustdock] = useState<boolean>(false);

  const [submissionDate, setSubmissionDate] = useState<String>(entryFormInfo.submissionDate);

  useEffect(() => {
    if (!personPlanId) {
      return;
    }
    const script = document.createElement('script');

    script.src = process.env.REACT_APP_TRUSTDOCK_HELPER_URL || 'https://helper.test.trustdock.io/v2/verification_helper.js';
    script.async = true;
    script.onload = () => {
      if (isLoadedTrustdock) {
        removeTrustdock();
        console.log("Trust Dock removed : ");
      }
      const trustdockInstance = new Trustdock({
        publicId: entryFormInfo.publicId,
        plans: [
          {
            id: personPlanId,
          },
        ],
        openerSelector: '.opener',
      });

      console.log("TrustDock Instance : ", trustdockInstance);

      setTrustdock(trustdockInstance);
      if(trustdockInstance.isLoaded) {
        setIsLoadedTrustdock(true);
      } else {
        trustdockInstance.on('loaded', () => {
          console.log("Trust Dock loaded : ");
          setIsLoadedTrustdock(true);
        });
      }
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    }
  }, [personPlanId]);

  const handleVerification = () => {
    if(trustdock) {
      trustdock.open();
    }
  }

  useEffect(() => {
    if(trustdock)
    {
      //// Processing when an event is received
      // Describe the process when identity verification documents have been submitted
      // For example, forcing a transition to the next page for users who do not close the modal after submission, etc.
      trustdock.on('documentsubmitted', documentsubmittedAction)
      // Describes the process when identity verification documents have been submitted and the modal is closed
      trustdock.on('completed', completedAction)
      // Describes what to do if the modal is closed without submitting identification documents
      trustdock.on('canceled', canceledAction)
    }
  },[trustdock]);

  const documentsubmittedAction = () => {
    console.log('received the documentsubmitted event')
    setIsLoading(false);
  }
  const canceledAction = () => {
    console.log('received the canceled event');
    setIsLoading(false);
    window.alert('Trustdock Uploader is failed');
  }
  const completedAction = async () => {
    setSubmitError(false);
    try {
      const submitFormResult = await submitFormInput({
        variables: {
          externalId: entryFormInfo.externalId,
          personalPublicId: entryFormInfo.publicId,
          planId: personPlanId,
          personalLastName: entryFormInfo.lastName,
          personalFirstName: entryFormInfo.firstName,
          personalLastNameKana: entryFormInfo.lastNameKa,
          personalFirstNameKana: entryFormInfo.firstNameKa,
          personalBirthday: entryFormInfo.birthday,
          personalPostalCode: entryFormInfo.postalCode,
          personalPrefecture: entryFormInfo.prefecture,
          personalCity: entryFormInfo.city,
          personalStreet: entryFormInfo.address,
          personalBuilding: entryFormInfo.building || '',
          personalGender: entryFormInfo.gender,
          personalPhone: entryFormInfo.phoneNum,
          personalEmail: entryFormInfo.email
        }
      });

      dispatch(setField({field: 'submissionDate', value: submitFormResult.data.submitFormInput}));
      dispatch(setField({field: 'entryFormStep', value: 'complete'}));
      navigate('/form/personal/complete');
    } catch (e) {
      console.log(e);
      setSubmitError(true);
    } finally {
      setIsLoading(false);
    }
  }

  const removeTrustdock = () => {
    // Delete registered listeners with `trustdock.off`
    // Note that an anonymous function cannot be specified as a target for deletion, so it cannot be deleted.
    trustdock.off('documentsubmitted', documentsubmittedAction)
    trustdock.off('completed', completedAction)
    trustdock.off('canceled', canceledAction)
    // Delete generated DOM elements
    trustdock.remove()
    // It is possible to delete the file and use it again in another process by `new Trustdock()`.
  }

  if(data) {
    dispatch(setField({field: 'externalId', value: data.newVerification.externalId}));
    dispatch(setField({field: 'publicId', value: data.newVerification.publicId}));
    dispatch(setField({field: 'planId_E_KYC_HO', value: (data.newVerification.plans.find((item: any) => item.name === 'E_KYC_HO')?.id) || ''}));
    dispatch(setField({field: 'planId_CORPORATE_NUMBER', value: (data.newVerification.plans.find((item: any) => item.name === 'CORPORATE_NUMBER')?.id) || ''}));
    dispatch(setField({field: 'planId_PUBLIC_PERSONAL_AUTHENTICATION', value: (data.newVerification.plans.find((item: any) => item.name === 'PUBLIC_PERSONAL_AUTHENTICATION')?.id) || ''}));
    dispatch(setField({field: 'planId_CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS', value: (data.newVerification.plans.find((item: any) => item.name === 'CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS')?.id) || ''}));
    dispatch(setField({field: 'planId_SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY', value: (data.newVerification.plans.find((item: any) => item.name === 'SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY')?.id) || ''}));
  }

  useEffect(() => {
    if (data && !personPlanId) {
      if (entryFormInfo.planId_E_KYC_HO) {
        // eKYC が利用可能な場合は、ユーザーのラジオ選択に任せる（デフォルト選択しない）
        return;
      }
      // eKYC がない場合は、公的個人認証を必ずデフォルト選択する
      if (entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION) {
        setPersonPlanId(entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION);
      }
    }
  }, [data, entryFormInfo.planId_E_KYC_HO, entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION, personPlanId]);

  if (loading) return <Loading />;
  if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

  return (
    <>
      <div className="bg-gray-50">
        <EntryFormHeader currentStep={3} />
        <main className="guide-container">
          <div className="mb-8">
            <h1 className="text-2xl font-bold">本人確認の手順</h1>
            {entryFormInfo.planId_E_KYC_HO && (
              <p className="text-gray-600 mt-2">以下の確認方法から1つお選びください。</p>
            )}
          </div>

          {entryFormInfo.planId_E_KYC_HO && (
          <div
            onClick={() => setPersonPlanId(entryFormInfo.planId_E_KYC_HO)}
            className={`border-2 rounded-lg p-4 mb-4 cursor-pointer transition ${
              personPlanId === entryFormInfo.planId_E_KYC_HO
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-white hover:border-blue-300"
            }`}
          >
            <label htmlFor="ekyc" className="cursor-pointer block">
              <div className="flex items-center mb-4">
                <div className={`w-5 h-5 rounded-full border-solid border-2 flex items-center justify-center mr-3 ${personPlanId === entryFormInfo.planId_E_KYC_HO ? 'border-blue-500' : 'border-gray-500'}`}>
                  <div className={`w-3 h-3 bg-blue-500 rounded-full ${personPlanId === entryFormInfo.planId_E_KYC_HO ? 'block' : 'hidden'}`}></div>
                </div>
                <h3 className={`text-lg font-semibold ${personPlanId === entryFormInfo.planId_E_KYC_HO ? 'text-blue-500' : ''}`}>eKYC本人確認</h3>
              </div>

              <div className="ml-9">
                <p className="text-gray-600 mb-4">
                  スマートフォンなどのカメラを使用して、その場で本人確認書類と顔写真を撮影する方法です。本人確認書類に記載された情報と顔写真を照合することで、オンラインでの本人確認を完了します。
                </p>

                <h4 className="font-semibold mb-2">利用可能な本人確認書類</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>運転免許証（有効期限内のもの）</li>
                  <li>マイナンバーカード（有効期限内のもの）</li>
                  <li>在留カード（有効期限内のもの）※外国籍の方</li>
                  <li>特別永住者証明書（有効期限内のもの）</li>
                </ul>

                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <h4 className="font-semibold mb-2">準備するもの</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>カメラ付きのスマートフォンまたはパソコン</li>
                    <li>上記いずれかの本人確認書類</li>
                    <li>明るい場所（本人確認書類が明瞭に撮影できる環境）</li>
                  </ul>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                  <h4 className="font-semibold text-amber-800 mb-2">確認時の注意点</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-amber-700">
                    <li>本人確認書類は、お手元の原本をご用意ください</li>
                    <li>カメラに映る文字情報が明瞭に読み取れるようにしてください</li>
                    <li>照明などで反射せず、影がない状態で撮影してください</li>
                    <li>顔写真は、無帽で正面を向いた状態で撮影してください</li>
                  </ul>
                </div>
              </div>
            </label>
          </div>
          )}

          <div
            onClick={() => setPersonPlanId(entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION)}
            className={`border-2 rounded-lg p-4 mb-4 cursor-pointer transition ${
              personPlanId === entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-white hover:border-blue-300"
            }`}
          >
            <input type="radio" id="jpki" name="corporateMethod" value="number" className="hidden" required/>
            <label htmlFor="jpki" className="cursor-pointer block">
              {/* Header Section */}
              <div className="flex items-center mb-4">
                <div className={`w-5 h-5 rounded-full border-solid border-2 flex items-center justify-center mr-3 ${personPlanId === entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION ? 'border-blue-500' : 'border-gray-500'}`}>
                  <div className={`w-3 h-3 bg-blue-500 rounded-full ${personPlanId === entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION ? 'block' : 'hidden'}`}></div>
                </div>
                <h3 className={`text-lg font-semibold ${personPlanId === entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION ? 'text-blue-500' : ''}`}>マイナンバーカードによる公的個人認証</h3>
              </div>

              {/* Description Section */}
              <div className="ml-9">
                <p className="text-gray-600 mb-4">
                  マイナンバーカードのICチップに格納された電子証明書を利用して本人確認を行う方法です。高度な暗号技術により、安全・確実な本人確認が可能です。
                </p>

                {/* Preparation Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">準備するもの</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>マイナンバーカード（有効期限内のもの）</li>
                    <li>マイナンバーカード対応スマートフォン</li>
                    <li>利用者証明用電子証明書のパスワード（6〜16桁の英数字）</li>
                    <li>マイナンバーカード読み取り用のアプリケーション</li>
                  </ul>
                </div>

                {/* Smartphone Support Section */}
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">対応スマートフォンについて</h4>
                  <p className="text-gray-600 mb-2">
                    マイナンバーカードの読み取りに対応したスマートフォンであれば、本人確認が可能です。
                    対応機種は
                    <a
                      href="https://www.jpki.go.jp/prepare/reader_writer.html"
                      target="_blank"
                      className="text-blue-600 hover:underline"
                    >
                      こちら
                    </a>
                    でご確認いただけます。
                  </p>
                  <p className="text-sm text-gray-500">
                    ※ iPhone（iOS 13.0以上）またはAndroid（Android 8.0以上）で、NFC Type-A/B/Fに対応している必要があります
                  </p>
                </div>

                {/* Notes Section */}
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                  <h4 className="font-semibold text-amber-800 mb-2">確認時の注意点</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-amber-700">
                    <li>電子証明書が有効期限内であることをご確認ください</li>
                    <li>パスワードを3回連続で間違えるとロックされます</li>
                    <li>ロックされた場合は市区町村窓口もしくはコンビニエンスストアでの手続きが必要です</li>
                    <li>スマートフォンのNFC機能をオンにしてください</li>
                  </ul>
                </div>
              </div>
            </label>
          </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <h3 className="font-semibold text-yellow-800 mb-2">重要な注意事項</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>提出された書類や情報に不備がある場合は、確認に時間がかかる場合があります</li>
                <li>確認完了までは、一部のサービス機能が制限される場合があります</li>
                <li>虚偽の情報を提出した場合、法的措置の対象となる可能性があります</li>
                <li>取得した個人情報は、本人確認の目的以外には使用いたしません</li>
              </ul>
            </div>

          {submitError && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-6" role="alert">
              <p className="text-sm text-red-700 mb-3">
                送信中にエラーが発生しました。通信環境をご確認のうえ、再度お試しください。入力内容は保持されています。
              </p>
              <button
                type="button"
                id="retryButton"
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
                onClick={() => {
                  setIsLoading(true);
                  completedAction();
                }}
              >
                {isLoading ? '送信中...' : '再送信する'}
              </button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <button
              type="button"
              id="startButton"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!personPlanId || !isLoadedTrustdock}
              onClick={() => {
                setIsLoading(true)
                if(!isLoading)
                  handleVerification()
              }}
            >
              {isLoading ? '読み込み中...' : '本人確認を開始する'}
            </button>
          </div>
        </main>
      </div>
    </>
  );
};

export default PersonalEntryFormGuide;
